import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SystemLogs.css';

const LEVEL_COLORS = {
  info:    { bg: 'rgba(66,153,225,0.15)',  text: '#4299e1', label: 'INFO' },
  warning: { bg: 'rgba(246,173,85,0.15)', text: '#f6ad55', label: 'WARN' },
  error:   { bg: 'rgba(252,129,129,0.15)',text: '#fc8181', label: 'ERR ' },
  debug:   { bg: 'rgba(159,122,234,0.12)',text: '#9f7aea', label: 'DBG ' },
};

const SOURCES = ['system', 'telemetry', 'neptune', 'rdf', 'graph', 'api', 'alert', 'auth'];

// Rich factory-accurate seed messages
const SEED_MESSAGES = [
  // Boot / init
  { level: 'info',    source: 'system',    text: 'bioverseAI Digital Twin v2.4.1 initialized — Ball Bearing Factory' },
  { level: 'info',    source: 'rdf',       text: 'RDF parser loaded Turtle ontology: 163 triples, 27 components, 3 lines' },
  { level: 'info',    source: 'neptune',   text: 'Neptune SPARQL endpoint healthy — digitaltwin-neptune.cluster-c4jkok8wwflz.us-east-1.neptune.amazonaws.com:8182' },
  { level: 'info',    source: 'telemetry', text: 'Telemetry service started — polling 27 components at 30s intervals' },
  { level: 'debug',   source: 'graph',     text: 'Cytoscape.js instance created — COSE layout, 39 nodes, 44 edges' },
  { level: 'info',    source: 'api',       text: 'GET /api/telemetry/latest → 200 OK (27 components, 108 readings)' },
  // Warnings — real component IDs from factory model
  { level: 'warning', source: 'telemetry', text: 'C-111 [Wheel 111 / M-002]: temperature 91.4°C — exceeds warn threshold 90°C' },
  { level: 'warning', source: 'telemetry', text: 'C-122 [Frame 122 / M-003]: vibration 112.3 Hz — approaching warn threshold 120 Hz' },
  { level: 'warning', source: 'telemetry', text: 'C-113 [Sensor 113 / M-002]: pressure 63.2 PSI — warn threshold 60 PSI' },
  { level: 'warning', source: 'telemetry', text: 'C-132 [Motor 132 / M-004]: temperature 95.1°C — exceeds warn threshold 90°C' },
  { level: 'warning', source: 'telemetry', text: 'C-151 [Cam 151 / M-006]: speed 78.2 RPM — exceeds warn threshold 75 RPM' },
  { level: 'warning', source: 'telemetry', text: 'C-171 [Wheel 171 / M-008]: vibration 133.4 Hz — exceeds warn threshold 120 Hz' },
  // Criticals
  { level: 'error',   source: 'telemetry', text: 'C-131 [Spindle 131 / M-004]: temperature 103.2°C — CRITICAL threshold 100°C' },
  { level: 'error',   source: 'telemetry', text: 'C-131 [Spindle 131 / M-004]: pressure 67.8 PSI — CRITICAL threshold 70 PSI' },
  { level: 'error',   source: 'telemetry', text: 'C-131 [Spindle 131 / M-004]: vibration 138.1 Hz — CRITICAL threshold 140 Hz' },
  // Alert events
  { level: 'error',   source: 'alert',     text: 'CRITICAL ALERT generated: C-131 Spindle 131 — Inner Ring Line M-004 — 3 sensors breached' },
  { level: 'info',    source: 'alert',     text: 'WARNING ALERT generated: C-171 Wheel 171 — Ball Element Line M-008 — vibration 133 Hz' },
  { level: 'info',    source: 'alert',     text: 'WARNING ALERT generated: C-111 Wheel 111 — Outer Ring Line M-002 — temp 91°C' },
  // Neptune / graph queries
  { level: 'info',    source: 'neptune',   text: 'SPARQL SELECT: components in Inner Ring line → 9 results (12ms)' },
  { level: 'info',    source: 'neptune',   text: 'SPARQL SELECT: dt:dependsOn graph of C-131 → 3 upstream dependencies' },
  { level: 'debug',   source: 'neptune',   text: 'SPARQL UPDATE: upsert telemetry snapshot for 27 components → 200 OK (45ms)' },
  { level: 'info',    source: 'rdf',       text: 'SPARQL query: SELECT ?comp ?machine WHERE { ?comp ex:componentOf ?machine } → 27 rows' },
  { level: 'debug',   source: 'rdf',       text: 'RDF writer serialized 163 triples to Turtle format (2.1 KB)' },
  // API calls
  { level: 'info',    source: 'api',       text: 'POST /api/chat → Claude Opus 4.6 streaming response started (tool: execute_sparql)' },
  { level: 'info',    source: 'api',       text: 'GET /api/rdf-models → 200 OK (5 ontology models)' },
  { level: 'info',    source: 'api',       text: 'GET /api/triples/latest → 200 OK (163 triples from Neptune)' },
  { level: 'info',    source: 'api',       text: 'GET /api/chat/status → 200 OK (neptune: connected, claude: ready)' },
  { level: 'debug',   source: 'auth',      text: 'AWS SigV4 token refreshed for Neptune IAM auth (expires 900s)' },
  // Telemetry cycle
  { level: 'info',    source: 'telemetry', text: 'Poll cycle #47 complete — 27 readings updated, 4 anomalies detected' },
  { level: 'info',    source: 'telemetry', text: 'Baseline drift detected: C-102 Motor 102 temp trend +2.3°C over 10 min' },
  { level: 'debug',   source: 'telemetry', text: 'Sensor smoothing applied: EWM α=0.2 over 6 readings per component' },
  // Graph events
  { level: 'debug',   source: 'graph',     text: 'Propagation highlight: C-131 → dt:dependsOn → C-132 → dt:propagates → C-133' },
  { level: 'info',    source: 'graph',     text: 'Impact analysis: C-131 failure affects 5 downstream components (depth 2)' },
  { level: 'debug',   source: 'graph',     text: 'Graph layout recalculated: COSE (36 nodes, 850ms)' },
  // System
  { level: 'info',    source: 'system',    text: 'CloudFront cache refreshed — frontend build 9fd3c1f deployed' },
  { level: 'info',    source: 'system',    text: 'Neptune cluster status: AVAILABLE — serverlessV2 min=1 max=8 NCU' },
];

// Live log templates — generate realistic dynamic entries
const LIVE_TEMPLATES = [
  () => ({ level: 'info',    source: 'telemetry', text: `Poll cycle complete — 27 readings updated, ${1 + Math.floor(Math.random()*5)} anomalies` }),
  () => ({ level: 'debug',   source: 'neptune',   text: `SPARQL SELECT latency: ${10 + Math.floor(Math.random()*30)}ms` }),
  () => ({ level: 'info',    source: 'api',       text: `GET /api/telemetry/latest → 200 OK (${100 + Math.floor(Math.random()*10)}ms)` }),
  () => ({ level: 'debug',   source: 'graph',     text: `Telemetry overlay refreshed — ${Math.floor(Math.random()*3) + 1} node colours changed` }),
  () => ({ level: 'warning', source: 'telemetry', text: `C-131 temperature ${(101 + Math.random()*4).toFixed(1)}°C — CRITICAL` }),
  () => ({ level: 'warning', source: 'telemetry', text: `C-111 temperature ${(90 + Math.random()*3).toFixed(1)}°C — WARNING` }),
  () => ({ level: 'info',    source: 'neptune',   text: `Heartbeat OK — ${163 + Math.floor(Math.random()*5)} triples in graph` }),
  () => ({ level: 'debug',   source: 'rdf',       text: `Ontology validation passed — 0 SHACL violations` }),
  () => ({ level: 'info',    source: 'alert',     text: `Alert digest: ${Math.floor(Math.random()*2)+1} critical, ${Math.floor(Math.random()*4)+2} warning` }),
  () => ({ level: 'info',    source: 'api',       text: `POST /api/chat → tool execute_sparql called, ${Math.floor(Math.random()*10)+2} results` }),
  () => ({ level: 'debug',   source: 'auth',      text: `Session token valid (${Math.floor(Math.random()*800)+100}s remaining)` }),
  () => ({ level: 'info',    source: 'system',    text: `Memory: ${Math.floor(Math.random()*200)+600}MB heap, ${Math.floor(Math.random()*20)+5}% GC` }),
];

let _logCounter = 0;
const makeLog = () => {
  _logCounter++;
  const tpl = LIVE_TEMPLATES[Math.floor(Math.random() * LIVE_TEMPLATES.length)];
  const entry = tpl();
  return { id: _logCounter, ...entry, timestamp: new Date() };
};

const seedLogs = () => {
  const now = Date.now();
  return SEED_MESSAGES.map((msg, i) => ({
    id: ++_logCounter,
    ...msg,
    timestamp: new Date(now - (SEED_MESSAGES.length - i) * 12000),
  }));
};

const SystemLogs = ({ telemetryData = [] }) => {
  const [logs, setLogs] = useState(() => seedLogs());
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const listRef = useRef(null);
  const pausedRef = useRef(false);

  pausedRef.current = paused;

  // Simulate live log ingestion
  useEffect(() => {
    const interval = setInterval(() => {
      if (pausedRef.current) return;
      setLogs(prev => {
        const next = [...prev, makeLog()];
        return next.slice(-500); // keep last 500
      });
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  // Generate logs from telemetry alerts
  useEffect(() => {
    if (telemetryData.length === 0) return;
    telemetryData.forEach(d => {
      if ((d.sensorAReading || 0) > 100) {
        setLogs(prev => [...prev, makeLog({
          level: 'error',
          source: 'telemetry',
          text: `Component #${d.componentID}: CRITICAL temperature ${d.sensorAReading?.toFixed(1)}°C`,
        })].slice(-500));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && listRef.current && !paused) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [logs, autoScroll, paused]);

  const clearLogs = useCallback(() => setLogs([]), []);

  const exportLogs = useCallback(() => {
    const text = logs
      .map(l => `[${l.timestamp.toISOString()}] [${l.level.toUpperCase()}] [${l.source}] ${l.text}`)
      .join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `digital-twin-logs-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs]);

  const filtered = logs.filter(l => {
    if (filter !== 'all' && l.level !== filter) return false;
    if (sourceFilter !== 'all' && l.source !== sourceFilter) return false;
    if (search && !l.text.toLowerCase().includes(search.toLowerCase()) &&
        !l.source.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const counts = logs.reduce((acc, l) => {
    acc[l.level] = (acc[l.level] || 0) + 1;
    return acc;
  }, {});

  const fmt = (ts) => ts.toLocaleTimeString('en-US', { hour12: false, millisecond: 'numeric' }) +
    '.' + String(ts.getMilliseconds()).padStart(3, '0');

  return (
    <div className="system-logs">
      {/* Header */}
      <div className="logs-header">
        <h2>System Logs</h2>
        <div className="logs-live-indicator">
          <span className={`live-dot${paused ? ' paused' : ''}`} />
          {paused ? 'Paused' : 'Live'}
        </div>
      </div>

      {/* Summary badges */}
      <div className="log-summary">
        {Object.entries(LEVEL_COLORS).map(([level, cfg]) => (
          <button
            key={level}
            className={`summary-badge${filter === level ? ' active' : ''}`}
            style={{ '--badge-color': cfg.text }}
            onClick={() => setFilter(prev => prev === level ? 'all' : level)}
          >
            <span className="badge-label">{cfg.label}</span>
            <span className="badge-count">{counts[level] || 0}</span>
          </button>
        ))}
        <span className="total-count">{logs.length} total</span>
      </div>

      {/* Toolbar */}
      <div className="logs-toolbar">
        <input
          className="log-search"
          type="text"
          placeholder="Search logs..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="source-filter"
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
        >
          <option value="all">All sources</option>
          {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="log-action-btn" onClick={() => setPaused(p => !p)}>
          {paused ? '▶ Resume' : '⏸ Pause'}
        </button>
        <button className="log-action-btn" onClick={() => setAutoScroll(p => !p)}>
          {autoScroll ? '📌 Pinned' : '📌 Pin'}
        </button>
        <button className="log-action-btn" onClick={exportLogs}>⬇ Export</button>
        <button className="log-action-btn danger" onClick={clearLogs}>🗑 Clear</button>
      </div>

      {/* Log list */}
      <div className="log-list" ref={listRef}>
        {filtered.length === 0 ? (
          <div className="log-empty">No matching log entries</div>
        ) : (
          filtered.map(log => {
            const cfg = LEVEL_COLORS[log.level] || LEVEL_COLORS.info;
            return (
              <div key={log.id} className="log-entry" style={{ '--level-color': cfg.text }}>
                <span className="log-ts">{fmt(log.timestamp)}</span>
                <span className="log-level" style={{ background: cfg.bg, color: cfg.text }}>
                  {cfg.label}
                </span>
                <span className="log-source">[{log.source}]</span>
                <span className="log-text">{log.text}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SystemLogs;
